import { AlertTriangle, Eye, MousePointer } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props {
  open: boolean;
  onCancel: () => void;
  onAccept: () => void;
  durationMin: number;
}

const RULES = [
  {
    icon: Eye,
    text: (
      <>
        No podés <strong>cambiar de pestaña ni de aplicación</strong>.
      </>
    ),
  },
  {
    icon: MousePointer,
    text: (
      <>
        No podés <strong>perder el foco de la ventana</strong> (clickear en
        otra app, abrir DevTools en ventana aparte, etc.).
      </>
    ),
  },
];

export function AntiCheatModal({
  open,
  onCancel,
  onAccept,
  durationMin,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg border-warning/40">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-5 w-5" />
            Antes de comenzar
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-foreground/90">
          Una vez que inicies el ejercicio se aplican las siguientes{" "}
          <strong>reglas estrictas</strong>:
        </p>

        <ul className="space-y-2.5 text-sm text-foreground/90">
          {RULES.map((r, i) => {
            const Icon = r.icon;
            return (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-destructive/15 text-destructive shrink-0">
                  <Icon className="h-3 w-3" />
                </span>
                <span>{r.text}</span>
              </li>
            );
          })}
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-warning/15 text-warning shrink-0">
              <AlertTriangle className="h-3 w-3" />
            </span>
            <span>
              Tenés <strong>{durationMin} minutos</strong> para completar el
              ejercicio. Cuando se termina, enviamos lo que hayas hecho hasta
              ese momento.
            </span>
          </li>
        </ul>

        <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive/90 leading-relaxed">
          La primera vez que rompas las reglas te avisamos. La{" "}
          <strong>segunda vez</strong> el ejercicio se cierra automáticamente y
          se envía tu última versión al evaluador marcada como invalidada.
        </div>

        <p className="text-xs text-muted-foreground">
          Cada violación queda registrada con su tipo y horario para que el
          evaluador tenga contexto.
        </p>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="default" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onAccept}>
            Acepto, comenzar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
